import React from 'react';
import pt from 'prop-types';
import { MrInputRadio, MrInputRadioGroup, MrInputText } from '@ion/components';
import { Box, Typography } from '@metarouter-private/mr-mui';
import { COOKIE_ENCODINGS, DEFAULT_COOKIE_ENCODING } from 'app/components/analytics-builder/CONSENT_SOURCE_TYPES';
import { CONSENT_COOKIE_ENCODING, CONSENT_COOKIE_WINDOW_VAR } from 'app/components/analytics-builder/form-keys';

const ENCODING_EXAMPLES = {
  [COOKIE_ENCODINGS.URL_ENCODED_KEY_VALUE]: 'groups=C0001%3A1%2CC0002%3A1',
  [COOKIE_ENCODINGS.PLAIN_KEY_VALUE]: 'C0001:1,C0002:0',
};
const EXAMPLE_COOKIE_NAME = 'OptanonConsent';

export default function ConsentSourceCookieFields({
  formState,
  setField,
  validateField,
  validateRequired,
  resetError,
}) {
  const selectedEncoding = formState[CONSENT_COOKIE_ENCODING]?.value ?? DEFAULT_COOKIE_ENCODING;

  return (
    <>
      <MrInputText
        label="Cookie Name"
        onChange={setField}
        value={formState.cookieName?.value ?? ''}
        onBlur={validateField}
        onInput={resetError}
        name="cookieName"
        errorMsg={formState.cookieName?.errorMsg}
        validators={[validateRequired()]}
        helperText={`Add the cookie name that your consent management solution uses to store user consent preferences. For example, if using OneTrust, this would be ${EXAMPLE_COOKIE_NAME}.`}
        placeholder={EXAMPLE_COOKIE_NAME}
        fullWidth
        required
      />
      <MrInputRadioGroup
        overrideValue={selectedEncoding}
        name={CONSENT_COOKIE_ENCODING}
        label="Cookie Encoding"
        helperText="Match the format of your consent cookie's value. To check, open your site in the browser, then DevTools → Application → Cookies, and look at the cookie's value."
        onChange={setField}
        resetError={resetError}
        required
      >
        <MrInputRadio
          value={COOKIE_ENCODINGS.URL_ENCODED_KEY_VALUE}
          label="URL-Encoded (OneTrust Default)"
          tooltip="The value contains percent-encoded characters like %3A and %2C — for example groups=C0001%3A1%2CC0002%3A1. This is how OneTrust writes its OptanonConsent cookie."
        />
        <MrInputRadio
          value={COOKIE_ENCODINGS.PLAIN_KEY_VALUE}
          label="Plain Text"
          tooltip="The value is readable as-is — for example C0001:1,C0002:0 — with no percent-encoded characters."
        />
      </MrInputRadioGroup>
      <Box mb={3}>
        <Typography variant="body2" color="text.secondary">
          Your cookie&apos;s value should look like: <code>{ENCODING_EXAMPLES[selectedEncoding]}</code>
        </Typography>
      </Box>
      <MrInputText
        label="Window Variable"
        onChange={setField}
        value={formState[CONSENT_COOKIE_WINDOW_VAR]?.value ?? ''}
        onBlur={validateField}
        onInput={resetError}
        name={CONSENT_COOKIE_WINDOW_VAR}
        errorMsg={formState[CONSENT_COOKIE_WINDOW_VAR]?.errorMsg}
        helperText="Optional. If your CMP also mirrors the consent string to a window variable, the built file reads that variable first and falls back to the cookie. Uses the same encoding selected above."
        fullWidth
      />
    </>
  );
}

ConsentSourceCookieFields.propTypes = {
  formState: pt.object.isRequired,
  setField: pt.func.isRequired,
  validateField: pt.func.isRequired,
  validateRequired: pt.func.isRequired,
  resetError: pt.func.isRequired,
};
